import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { captureException, withScope } from '@sentry/browser';
import { Redirect } from 'react-router';
import { useParams, useHistory } from 'react-router-dom';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { appClient } from 'src/config/apollo';
import { colors, layout } from '@modernhealth/vital-foundation';

import {
    ContentCardsRowSkeleton,
    PageHeader,
    PageHeaderSkeleton,
    SkeletonPlaceholder,
    SectionHeaderSkeleton,
    HeadingText40,
    pxToRem,
} from '@modernhealth/vital-components';

import { RenderScreenSectionNode } from 'src/modules/dashboard/components/ContentCard';
import { ScreenSectionFragment } from 'src/modules/dashboard/documents/getHomeScreen.generated';

import { useGetResourcesPageQuery } from '../documents/getResourcesPage.generated';
import { useGetExplorePageQuery } from '../documents/getExplorePage.generated';

type ExploreTabsNames = 'topics' | 'activities';

type ExploreTabs = {
    key: ExploreTabsNames;
    name: string;
}[];

type ExploreParams = {
    activeTab: ExploreTabsNames;
};

const Wrapper = styled.div({
    width: '100%',
    maxWidth: layout.containerWidth,
    margin: '0 auto',
    paddingLeft: layout.containerPaddingX,
    paddingRight: layout.containerPaddingX,
    paddingBottom: layout.footerHeight,
});

const TabsWrapper = styled.div({
    width: '100%',
    display: 'flex',
    borderBottom: `2px solid ${colors.gray05}`,
    marginTop: pxToRem(35),
});
interface TabElementProps {
    isLastTab?: boolean;
    isSelected?: boolean;
}

const TabElement = styled.button<TabElementProps>(
    () => ({
        paddingBottom: pxToRem(8),
        paddingLeft: 0,
        paddingRight: 0,
        position: 'relative',
        border: 'none',
        background: 'none',
        '&:focus-visible': {
            outline: `1px solid ${colors.blue30}`,
        },
    }),
    props => (props.isLastTab ? {} : { marginRight: pxToRem(50) }),
    props =>
        props.isSelected
            ? {
                  '&:after': {
                      position: 'absolute',
                      content: '""',
                      bottom: -2,
                      left: 0,
                      height: 2,
                      width: '105%',
                      backgroundColor: colors.violet65,
                  },
              }
            : {},
);

export const Resources = () => {
    const flags = useFlags();

    return flags.showNewExplorePageWeb ? <Explore /> : <OldResources />;
};

export const Explore = () => {
    const { activeTab } = useParams<ExploreParams>();
    const history = useHistory();
    const { data, loading, error } = useGetExplorePageQuery({
        client: appClient,
        errorPolicy: 'all',
        fetchPolicy: 'cache-and-network',
    });
    const { t } = useTranslation();

    const tabs: ExploreTabs = [
        {
            key: 'topics',
            name: t('translation:topics'),
        },
        {
            key: 'activities',
            name: t('translation:activities'),
        },
    ];

    const getSelectedTabDefaultValue = () => {
        if (!activeTab) {
            return tabs[0].key;
        }

        if (!tabs.filter(tab => tab.key === activeTab).length) {
            return tabs[0].key;
        }

        return activeTab;
    };

    const [selectedTab, setSelectedTab] = useState(getSelectedTabDefaultValue());

    const exploreScreen = data?.exploreScreen;

    useEffect(() => {
        if (activeTab !== selectedTab) {
            history.push(`/resources/${selectedTab}`);
        }
    }, [selectedTab, activeTab, history]);

    useEffect(() => {
        if (error) {
            captureException(error);
        }
    }, [error]);

    useEffect(() => {
        if (!loading && !error && !data?.exploreScreen) {
            withScope(scope => {
                scope.setExtra('response', JSON.stringify(data));

                captureException(
                    new Error('Missing required data in the response to render explore page'),
                );
            });
        }
    }, [data, loading, error]);

    if (loading && !exploreScreen) {
        return (
            <Wrapper>
                <PageHeaderSkeleton />
                <ContentCardsRowSkeleton items={[{}, {}, {}, {}, {}]} />
                <SectionHeaderSkeleton showSecondaryButton />
                <ContentCardsRowSkeleton items={[{}, {}, {}, {}, {}]} />
                <SectionHeaderSkeleton showSecondaryButton />
                <ContentCardsRowSkeleton items={[{}, {}, {}, {}, {}]} />
                <SectionHeaderSkeleton showSecondaryButton />
                <ContentCardsRowSkeleton items={[{}, {}, {}, {}, {}]} />
            </Wrapper>
        );
    }

    if (!exploreScreen) {
        return <Redirect to="/" />;
    }

    return (
        <Wrapper>
            <PageHeader>{t('shared:header.explore')}</PageHeader>
            <TabsWrapper>
                {tabs.map(({ key, name }, i) => (
                    <TabElement
                        key={key}
                        isLastTab={i === tabs.length - 1}
                        isSelected={selectedTab === key}
                        onClick={() => setSelectedTab(key)}
                    >
                        <HeadingText40 fontColor={selectedTab === key ? 'gray85' : 'gray40'}>
                            {name}
                        </HeadingText40>
                    </TabElement>
                ))}
            </TabsWrapper>
            {selectedTab === tabs[0].key && exploreScreen?.topics
                ? exploreScreen.topics
                      .filter(section => section !== null)
                      .map(section => (
                          <RenderScreenSectionNode section={section} key={section.id} />
                      ))
                : null}

            {selectedTab === tabs[1].key && exploreScreen?.activities ? (
                <RenderScreenSectionNode
                    section={exploreScreen?.activities as ScreenSectionFragment}
                />
            ) : null}
        </Wrapper>
    );
};

export const OldResources = () => {
    const { data, loading, error } = useGetResourcesPageQuery({
        client: appClient,
        errorPolicy: 'all',
        fetchPolicy: 'cache-and-network',
    });

    const resourcesScreen = data?.resourcesScreen;

    const { t } = useTranslation();

    useEffect(() => {
        if (error) {
            captureException(error);
        }
    }, [error]);

    useEffect(() => {
        if (!loading && !error && !data?.resourcesScreen) {
            withScope(scope => {
                scope.setExtra('response', JSON.stringify(data));

                captureException(
                    new Error('Missing required data in the response to render resources page'),
                );
            });
        }
    }, [data, loading, error]);

    if (loading && !resourcesScreen) {
        return (
            <Wrapper>
                <PageHeaderSkeleton />
                <SkeletonPlaceholder width="100%" height={250} borderRadius={8} />
                <SectionHeaderSkeleton showSecondaryButton />
                <ContentCardsRowSkeleton items={[{}, {}, {}, {}, {}]} />
                <SectionHeaderSkeleton showSecondaryButton />
                <ContentCardsRowSkeleton items={[{}, {}, {}, {}, {}]} />
                <SectionHeaderSkeleton showSecondaryButton />
                <ContentCardsRowSkeleton items={[{}, {}, {}, {}, {}]} />
                <SectionHeaderSkeleton showSecondaryButton />
                <ContentCardsRowSkeleton items={[{}, {}, {}, {}, {}]} />
            </Wrapper>
        );
    }

    if (!resourcesScreen) {
        return <Redirect to="/" />;
    }

    return (
        <Wrapper>
            <PageHeader>{t('shared:header.explore')}</PageHeader>

            {resourcesScreen?.screenSections
                .filter((section): section is ScreenSectionFragment => section !== null)
                .map(section => (
                    <RenderScreenSectionNode section={section} key={section.id} />
                ))}
        </Wrapper>
    );
};
